import { addDays, daysBetween, firstOccurrence, weekdayOf, weeksBetween } from './schedule';

/**
 * Series of events (Kalender 2, 1.25): every rhythm the event form offers – daily, weekly on
 * some days, monthly on a date or "am 3. Montag", yearly – with an end (date or count), dates
 * taken out, single dates moved and an optional pause in the school holidays.
 *
 * A series without `freq` is weekly – that is what 1.24 and earlier stored, so old series read
 * the same. Daily and "werktags" are stored as weekly on all days / Mon–Fri, which also keeps them
 * right in an app that does not know `freq` yet.
 */
export interface SeriesRule {
  /** Weekly: 1 = Mon … 7 = Sun. Empty for monthly and yearly. */
  days: number[];
  freq?: 'daily' | 'weekly' | 'monthly' | 'yearly';
  /** Every n days / weeks / months / years. */
  every?: number;
  /** Monthly: on the same date ("am 19.") or the same weekday of the month ("am 3. Montag"). */
  monthBy?: 'date' | 'weekday';
  until?: string;
  /** Ends after this many dates (dates taken out still count, like in other calendars). */
  count?: number;
  /** Single dates taken out of the series. */
  skip?: string[];
  /** Training, courses … pause during the school holidays of the family's state. */
  skipHolidays?: boolean;
  /** "Nur diesen Termin" moved to another time. */
  moved?: Record<string, { start?: string; end?: string }>;
}

export interface SeriesContext {
  /** Is this date a school holiday or public holiday for the family? */
  schoolFree?: (date: string) => boolean;
}

/** "Am 3. Montag": the how-many-th weekday of its month (5 = the last one). */
export function weekdayOfMonth(date: string): number {
  return Math.ceil(Number(date.slice(8, 10)) / 7);
}

const monthsBetween = (a: string, b: string) =>
  (Number(b.slice(0, 4)) - Number(a.slice(0, 4))) * 12 +
  (Number(b.slice(5, 7)) - Number(a.slice(5, 7)));

/** Is `date` on the rhythm alone – before end, count, dates taken out and holidays? */
function onRhythm(start: string, r: SeriesRule, date: string): boolean {
  const every = Math.max(1, r.every ?? 1);
  const freq = r.freq ?? 'weekly';
  if (freq === 'daily') return date >= start && daysBetween(start, date) % every === 0;
  if (freq === 'weekly') {
    if (!r.days.length) return false;
    const first = firstOccurrence(start, r.days);
    return (
      date >= first && r.days.includes(weekdayOf(date)) && weeksBetween(first, date) % every === 0
    );
  }
  if (date < start) return false;
  if (freq === 'monthly') {
    if (monthsBetween(start, date) % every !== 0) return false;
    if (r.monthBy === 'weekday') {
      if (weekdayOf(date) !== weekdayOf(start)) return false;
      const n = weekdayOfMonth(start);
      // the 5th weekday of a month is "the last one": in shorter months the 4th counts
      if (n === 5) return addDays(date, 7).slice(0, 7) !== date.slice(0, 7);
      return weekdayOfMonth(date) === n;
    }
    // "am 31.": months without that date are left out, as in other calendars
    return date.slice(8) === start.slice(8);
  }
  // yearly (29.2. only in leap years)
  return (
    (Number(date.slice(0, 4)) - Number(start.slice(0, 4))) % every === 0 &&
    date.slice(5) === start.slice(5)
  );
}

/** How many dates of the rhythm fall on `from`…`date` (both included). */
function rhythmCount(start: string, r: SeriesRule, date: string): number {
  let n = 0;
  // a long daily series ends after a few years at most – enough for "nach … Terminen"
  for (let d = start, i = 0; d <= date && i < 4000; d = addDays(d, 1), i++) {
    if (onRhythm(start, r, d)) n++;
  }
  return n;
}

/** Does the series take place on `date`? */
export function inSeries(
  start: string,
  r: SeriesRule,
  date: string,
  ctx: SeriesContext = {},
): boolean {
  if (r.until && date > r.until) return false;
  if (r.skip?.includes(date)) return false;
  if (!onRhythm(start, r, date)) return false;
  if (r.skipHolidays && ctx.schoolFree?.(date)) return false;
  if (r.count && rhythmCount(start, r, date) > r.count) return false;
  return true;
}

/**
 * The next dates of a series from `from` on, for the form's preview – including the ones paused in
 * the holidays (marked), so the family sees why a week is missing.
 */
export function nextDates(
  start: string,
  r: SeriesRule,
  from: string,
  n: number,
  ctx: SeriesContext = {},
): { date: string; paused: boolean }[] {
  const out: { date: string; paused: boolean }[] = [];
  const plain = { ...r, skipHolidays: false };
  for (
    let d = from < start ? start : from, i = 0;
    out.length < n && i < 1500;
    d = addDays(d, 1), i++
  ) {
    if (!inSeries(start, plain, d)) continue;
    out.push({ date: d, paused: Boolean(r.skipHolidays && ctx.schoolFree?.(d)) });
  }
  return out;
}

/** The time on one date of a series – "Nur diesen Termin" may have moved it. */
export function seriesTime(
  r: SeriesRule,
  date: string,
  base: { start?: string; end?: string },
): { start?: string; end?: string } {
  const m = r.moved?.[date];
  // only the time – never the rest of `base` (a whole event would bring its first date along)
  return { start: m?.start ?? base.start, end: m?.end ?? base.end };
}

// ---------------------------------------------------------------------------------------------
// Shift plans
// ---------------------------------------------------------------------------------------------

/** A shift key of a plan: F, S, N, T (named by the family), "-" free, "U" holiday. */
export type ShiftKey = string;

export interface ShiftRhythm {
  /** First day of the rhythm ("Tag 1"). */
  start: string;
  until?: string;
  pattern: ShiftKey[];
  /** Only on this day: swapped, free ("-") or holiday ("U"). */
  over?: Record<string, ShiftKey>;
}

/** Which shift falls on `date` – null outside the plan. */
export function shiftOn(plan: ShiftRhythm, date: string): ShiftKey | null {
  if (date < plan.start || (plan.until && date > plan.until) || !plan.pattern.length) return null;
  const swapped = plan.over?.[date];
  if (swapped) return swapped;
  return plan.pattern[daysBetween(plan.start, date) % plan.pattern.length] ?? null;
}
